import type {
  SystemAiAssistantDefinition,
  SystemAiAssistantHandler,
  SystemAiAssistantRegistration,
} from './SystemWorkspaceContracts';

const registrations = new Map<
  string,
  SystemAiAssistantRegistration
>();

function registrationKey(
  systemId: string,
  assistantId: string,
): string {
  return `${systemId}:${assistantId}`;
}

export function registerSystemAiAssistant(
  definition: SystemAiAssistantDefinition,
  handler: SystemAiAssistantHandler,
): void {
  if (
    definition.id !==
    handler.assistantId
  ) {
    throw new Error(
      'AI assistant definition and handler IDs do not agree.',
    );
  }

  if (
    definition.systemId !==
    handler.systemId
  ) {
    throw new Error(
      'AI assistant and handler system authority do not agree.',
    );
  }

  const key = registrationKey(
    definition.systemId,
    definition.id,
  );

  if (registrations.has(key)) {
    throw new Error(
      `System AI assistant already registered: ${key}`,
    );
  }

  registrations.set(key, {
    definition,
    handler,
  });
}

export function getSystemAiAssistantRegistration(
  systemId: string,
  assistantId: string,
): SystemAiAssistantRegistration | null {
  return (
    registrations.get(
      registrationKey(
        systemId,
        assistantId,
      ),
    ) ?? null
  );
}

export function listSystemAiAssistantRegistrations(
  systemId?: string,
): SystemAiAssistantRegistration[] {
  const values = [
    ...registrations.values(),
  ];

  if (!systemId) {
    return values;
  }

  return values.filter(
    registration =>
      registration.definition.systemId ===
      systemId,
  );
}

export function hasSystemAiAssistantRegistration(
  systemId: string,
  assistantId: string,
): boolean {
  return registrations.has(
    registrationKey(
      systemId,
      assistantId,
    ),
  );
}

export function clearSystemAiAssistantRegistryForTests():
  void {
  registrations.clear();
}
